// SearchPanel — modal dialog that runs a global search across joined
// rooms and hands off to `onJump` when the user lands a result.
//
// Visuals follow docs/chat-design-brief.md § Global search: pinned
// 640×520 dialog on a dark scrim, 56px header with an input and a
// room-filter pill, grouped results, footer "Load more" when a
// `nextBatch` cursor exists. Every dimension, colour, and duration
// threads through `--chat-*` tokens.
//
// Behaviour:
//   - Opens via Cmd/Ctrl+K (listener mounted by the app shell).
//   - Escape closes; focus returns to the previously-focused element
//     via `useFocusTrap`.
//   - ↑/↓ move a keyboard-active row; Enter jumps.
//   - Clicking a result invokes `onJump(roomId, eventId)`.
//
// The component is presentational. It receives `search` (the SDK
// call), `rooms` (for the filter), `onJump`, and `onClose` so stories
// can render every state without a Matrix client.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { FileResultItem, SearchResultItem } from "~/components/SearchResultItem";
import { useFocusTrap } from "~/hooks/useFocusTrap";
import { useSearch, type SearchFunction } from "~/hooks/useSearch";
import type { RoomSummary, SearchResult, SearchResultKind } from "~/lib/matrix";
import { formatMessageTime } from "~/lib/message-utils";
import { isEmbeddedPortal } from "~/lib/portal-positioning";
import { useUnderline } from "~/lib/underline";
import { ICON_STROKE } from "~/lib/icon";

export interface SearchPanelProps {
  /** When false the component returns null. Keeping open-ness at the
   *  parent lets the shell decide the mount lifecycle (e.g. keep the
   *  last search state, or drop it on close). */
  open: boolean;
  /** Close handler. Invoked on Escape, scrim click, and the X button. */
  onClose: () => void;
  /** Search backend. In production: (q, r, n) => searchEvents(client, q, r, n).
   *  In stories: a fake that resolves with fixtures. */
  search: SearchFunction;
  /** Rooms the user has joined. Used for the room-filter dropdown and
   *  for enriching results with a room name when the server hasn't
   *  (rare, but the stub only carries names on its own seeded rooms). */
  rooms: readonly RoomSummary[];
  /** Invoked when the user commits a result. The shell flips the
   *  active room, publishes a pending jump, and closes the dialog. */
  onJump: (roomId: string, eventId: string) => void;
  /** Seed the input with a query on open. Stories use this to show
   *  results-populated variants without driving the input character
   *  by character. Production callers should leave this undefined. */
  initialQuery?: string;
}

export function SearchPanel({
  open,
  onClose,
  search,
  rooms,
  onJump,
  initialQuery,
}: SearchPanelProps) {
  const [query, setQuery] = useState(initialQuery ?? "");
  const [roomFilter, setRoomFilter] = useState<string | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState(0);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const portalRoot = usePortalRoot();

  // Hide results from rooms outside the current `rooms` list. The
  // sidebar and filter dropdown already reflect the visible list, so
  // building a lookup from `rooms` here is the single source of truth.
  const scopedRoomIds = useMemo(() => new Set(rooms.map((r) => r.id)), [rooms]);
  const {
    results: rawResults,
    isSearching,
    nextBatch,
    searchMore,
  } = useSearch(search, query, roomFilter);
  const results = useMemo(
    () => rawResults.filter((r) => scopedRoomIds.has(r.roomId)),
    [rawResults, scopedRoomIds],
  );

  // Keyboard-active row resets to the first match whenever the
  // results swap — typing, room filter change, or pagination. Without
  // this, stale indices point past the end of the new results list
  // and the accent bar disappears.
  useEffect(() => {
    setActiveIndex(0);
  }, [results]);

  // Reset transient state when the dialog opens so the user always
  // starts from the empty prompt. Leaving stale query / filter bytes
  // in state after a close would feel like the panel "remembers" —
  // the brief treats each open as a fresh session. Stories that
  // seed a query via `initialQuery` re-enter the reset flow with
  // that seed rather than an empty string.
  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery ?? "");
    setRoomFilter(undefined);
    setActiveIndex(0);
  }, [open, initialQuery]);

  // Focus trap lands focus on the first focusable inside the dialog
  // — which is the search input — and restores focus to the
  // previously-active element on close. No separate auto-focus
  // effect needed.
  useFocusTrap(dialogRef, open);

  const grouped = useMemo(() => groupByDimension(results), [results]);
  // Display order (Messages then Files) drives ↑/↓ navigation.
  const flatResults = useMemo(() => grouped.flatMap((group) => group.results), [grouped]);

  // Escape-to-close. The dialog-level keydown handler also fires for
  // the input, so this is the single Escape owner regardless of which
  // child has focus. Arrow/Enter navigation only fires while the
  // search input itself has focus — otherwise the handler would
  // intercept Enter on the close / load-more / room-filter buttons
  // and swallow their native activation.
  const onDialogKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (flatResults.length === 0) return;
      const focusedOnInput = e.target === inputRef.current;
      if (!focusedOnInput) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % flatResults.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + flatResults.length) % flatResults.length);
      } else if (e.key === "Enter") {
        const pick = flatResults[activeIndex];
        if (pick) {
          e.preventDefault();
          onJump(pick.roomId, pick.eventId);
        }
      }
    },
    [activeIndex, onClose, onJump, flatResults],
  );

  // Flat index lookup by `eventId`, rebuilt only when the display
  // order changes. `Body` walks each group in render; without this
  // map it would call `flatResults.indexOf(r)` per row, turning render
  // cost into O(n²).
  const flatIndexByEventId = useMemo(() => {
    const m = new Map<string, number>();
    for (let i = 0; i < flatResults.length; i += 1) {
      const r = flatResults[i];
      if (r) m.set(r.eventId, i);
    }
    return m;
  }, [flatResults]);
  const totalResults = flatResults.length;
  // `aria-activedescendant` on the input points at the keyboard-
  // active option so screen readers announce it during ↑/↓ nav.
  // Undefined when the active index is out of bounds (empty
  // results) so we don't ship a dangling id.
  const activeDescendantId =
    flatResults.length > 0 && activeIndex >= 0 && activeIndex < flatResults.length
      ? `search-result-${flatResults[activeIndex]!.eventId}`
      : undefined;

  if (!open) return null;
  if (!portalRoot) return null;

  // Scrim click closes. Target-vs-current-target check avoids
  // `stopPropagation` on the dialog (which would otherwise block
  // outside-click detection for child popovers like the room filter
  // dropdown) — the scrim div owns this handler, children that
  // bubble up carry a target that is *inside* the dialog.
  const onScrimClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Embed-rooted portals position the scrim absolutely so it fills
  // the embed's bounding box rather than the host viewport (the embed
  // root carries `position: relative`). Body-rooted portals — both
  // the standalone shell registered via `EmbedPortalContext` and the
  // bare `document.body` fallback for isolated renders — keep the
  // viewport-fixed scrim. Same per-overlay flip pattern as
  // Dialog/DeleteConfirmDialog/ImageLightbox.
  const isEmbedded = isEmbeddedPortal(portalRoot);
  const scrimPosition: "fixed" | "absolute" = isEmbedded ? "absolute" : "fixed";
  // Embed mode also clamps the panel height to the surface so a tall
  // dialog inside a short embed stays visible (the embed root has
  // `overflow: hidden`, so an unconstrained 520px panel inside a
  // ~580px-or-shorter slot would have its footer / load-more clipped
  // and become unreachable). The `--chat-space-16` allowance matches
  // the scrim's `paddingTop` so the dialog never collides with the
  // embed's top edge. Standalone keeps the pinned 520px from the
  // brief — same approach the shared `Dialog` primitive takes.
  const panelHeight = isEmbedded
    ? `min(var(--chat-search-dialog-height), calc(100% - var(--chat-space-16)))`
    : "var(--chat-search-dialog-height)";

  const overlay = (
    <div
      role="presentation"
      onClick={onScrimClick}
      style={{
        position: scrimPosition,
        inset: 0,
        background: "var(--chat-scrim-dialog)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "var(--chat-space-16)",
        zIndex: "var(--chat-search-dialog-z-index)",
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search messages"
        onKeyDown={onDialogKeyDown}
        style={{
          width: "var(--chat-search-dialog-width)",
          maxWidth: "100%",
          height: panelHeight,
          display: "flex",
          flexDirection: "column",
          background: "var(--chat-surface-dialog)",
          borderRadius: "var(--chat-radius-xl)",
          border: "1px solid var(--chat-surface-border)",
          boxShadow: "var(--chat-shadow-dialog)",
          overflow: "hidden",
        }}
      >
        <Header
          inputRef={inputRef}
          query={query}
          onQueryChange={setQuery}
          roomFilter={roomFilter}
          onClearRoomFilter={() => setRoomFilter(undefined)}
          rooms={rooms}
          onSelectRoomFilter={setRoomFilter}
          onClose={onClose}
          activeDescendantId={activeDescendantId}
        />
        <Body
          query={query}
          isSearching={isSearching}
          groups={grouped}
          rooms={rooms}
          activeIndex={activeIndex}
          flatIndexByEventId={flatIndexByEventId}
          totalResults={totalResults}
          onPick={onJump}
        />
        {nextBatch !== null && !isSearching && query.trim().length > 0 && (
          <Footer onLoadMore={() => void searchMore()} />
        )}
      </div>
    </div>
  );

  return createPortal(overlay, portalRoot);
}

/* ─── header ──────────────────────────────────────────────────── */

function Header({
  inputRef,
  query,
  onQueryChange,
  roomFilter,
  onClearRoomFilter,
  rooms,
  onSelectRoomFilter,
  onClose,
  activeDescendantId,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  query: string;
  onQueryChange: (next: string) => void;
  roomFilter: string | undefined;
  onClearRoomFilter: () => void;
  rooms: readonly RoomSummary[];
  onSelectRoomFilter: (roomId: string) => void;
  onClose: () => void;
  activeDescendantId: string | undefined;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const underline = useUnderline();
  const activeRoom = rooms.find((r) => r.id === roomFilter);
  // Ref to the toggle button so the menu's pointerdown dismiss
  // listener can recognise clicks on the toggle itself — without
  // this, clicking the open pill would fire pointerdown (close) +
  // click (toggle) and the menu would close-then-reopen on the
  // same click.
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  // Stable identity for the dismiss callback so `RoomFilterMenu`'s
  // `useEffect` doesn't tear down / re-attach the pointerdown
  // listener on every parent render.
  const onMenuDismiss = useCallback(() => setDropdownOpen(false), []);
  const onMenuSelect = useCallback(
    (id: string | null) => {
      if (id === null) onClearRoomFilter();
      else onSelectRoomFilter(id);
      setDropdownOpen(false);
    },
    [onClearRoomFilter, onSelectRoomFilter],
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: "var(--chat-search-dialog-header-height)",
        padding: "var(--chat-space-3) var(--chat-space-4)",
        borderBottom: "1px solid var(--chat-surface-border)",
        flexShrink: 0,
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-2)",
          flex: 1,
          minWidth: 0,
          height: "var(--chat-search-dialog-input-height)",
          background: "transparent",
          border: "none",
          borderBottom: `1px solid ${underline.borderColor}`,
          transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
        }}
      >
        <Search
          size={20}
          strokeWidth={ICON_STROKE}
          aria-hidden
          style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          // ARIA combobox pattern: `aria-expanded` reflects popup
          // visibility, not whether an option is active. The listbox
          // is always rendered while the dialog is open (showing
          // empty / loading / no-results / results), so `expanded`
          // tracks the dialog itself. `aria-activedescendant` stays
          // conditional — that one *is* about the active option.
          aria-expanded
          aria-controls="search-results-listbox"
          aria-activedescendant={activeDescendantId}
          autoComplete="off"
          value={query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onQueryChange(e.target.value)}
          onFocus={underline.onFocus}
          onBlur={underline.onBlur}
          placeholder="Search messages"
          aria-label="Search messages"
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-lg-size)",
            lineHeight: "var(--chat-type-body-lg-line)",
          }}
        />
      </div>
      <RoomFilterPill
        activeLabel={activeRoom ? `#${activeRoom.name}` : "All rooms"}
        isScoped={!!roomFilter}
        onToggle={() => setDropdownOpen((v) => !v)}
        onClear={onClearRoomFilter}
        toggleRef={toggleRef}
      />
      <button
        type="button"
        aria-label="Close search"
        onClick={onClose}
        className="chat-button"
        style={{
          width: 28,
          height: 28,
          borderRadius: "var(--chat-radius-sm)",
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-secondary)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <X size={14} strokeWidth={ICON_STROKE} aria-hidden />
      </button>
      {dropdownOpen && (
        <RoomFilterMenu
          rooms={rooms}
          activeRoomId={roomFilter}
          onSelect={onMenuSelect}
          onDismiss={onMenuDismiss}
          toggleRef={toggleRef}
        />
      )}
    </div>
  );
}

function RoomFilterPill({
  activeLabel,
  isScoped,
  onToggle,
  onClear,
  toggleRef,
}: {
  activeLabel: string;
  isScoped: boolean;
  onToggle: () => void;
  onClear: () => void;
  toggleRef: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--chat-space-1)",
        height: 32,
        background: "transparent",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
        flexShrink: 0,
      }}
    >
      <button
        ref={toggleRef}
        type="button"
        onClick={onToggle}
        aria-label="Choose room filter"
        aria-haspopup="listbox"
        className="chat-button"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "var(--chat-space-1)",
          border: "none",
          background: "transparent",
          color: "inherit",
          font: "inherit",
          padding: 0,
          cursor: "pointer",
        }}
      >
        {activeLabel}
        <ChevronDown size={12} strokeWidth={ICON_STROKE} aria-hidden />
      </button>
      {isScoped && (
        <button
          type="button"
          aria-label="Clear room filter"
          onClick={onClear}
          className="chat-button"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 16,
            height: 16,
            border: "none",
            background: "transparent",
            color: "inherit",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <X size={10} strokeWidth={ICON_STROKE} aria-hidden />
        </button>
      )}
    </span>
  );
}

function RoomFilterMenu({
  rooms,
  activeRoomId,
  onSelect,
  onDismiss,
  toggleRef,
}: {
  rooms: readonly RoomSummary[];
  activeRoomId: string | undefined;
  onSelect: (roomId: string | null) => void;
  onDismiss: () => void;
  toggleRef: RefObject<HTMLButtonElement | null>;
}) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Close on any pointerdown outside the menu. `pointerdown` fires
  // before `click`, which is important: a click selection inside the
  // menu completes on mousedown+mouseup; using `click` here races
  // with the button's own click handler. Composed-path check handles
  // shadow-DOM targets and `target === menu-interior` equally well.
  //
  // Ignore pointerdowns on the toggle button — the button's own
  // onClick handler runs afterwards and owns the open/close toggle.
  // Without this, clicking the toggle while the menu is open would
  // fire pointerdown (close) + click (reopen), producing an
  // apparent no-op.
  useEffect(() => {
    function onDocPointer(e: PointerEvent) {
      const menu = menuRef.current;
      if (!menu) return;
      const path = e.composedPath();
      if (path.includes(menu)) return;
      const toggle = toggleRef.current;
      if (toggle && path.includes(toggle)) return;
      onDismiss();
    }
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, [onDismiss, toggleRef]);

  return (
    <div
      ref={menuRef}
      role="listbox"
      aria-label="Room filter"
      style={{
        position: "absolute",
        top: "calc(var(--chat-search-dialog-header-height) - var(--chat-space-1))",
        right: "var(--chat-space-4)",
        minWidth: "var(--chat-search-dialog-menu-min-width)",
        maxHeight: "var(--chat-search-dialog-menu-max-height)",
        overflowY: "auto",
        padding: "var(--chat-space-1)",
        background: "var(--chat-surface-overlay)",
        borderRadius: "var(--chat-radius-xl)",
        border: "1px solid var(--chat-surface-border)",
        boxShadow: "var(--chat-shadow-popover)",
        zIndex: 1,
      }}
    >
      <MenuItem label="All rooms" active={!activeRoomId} onSelect={() => onSelect(null)} />
      {rooms.map((r) => (
        <MenuItem
          key={r.id}
          label={`#${r.name}`}
          active={activeRoomId === r.id}
          onSelect={() => onSelect(r.id)}
        />
      ))}
    </div>
  );
}

function MenuItem({
  label,
  active,
  onSelect,
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onSelect}
      className="chat-button"
      style={{
        width: "100%",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        padding: "var(--chat-space-1) var(--chat-space-2)",
        borderRadius: "var(--chat-radius-sm)",
        background: active ? "var(--chat-surface-raised)" : "transparent",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
        border: "none",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

/* ─── body ────────────────────────────────────────────────────── */

function Body({
  query,
  isSearching,
  groups,
  rooms,
  activeIndex,
  flatIndexByEventId,
  totalResults,
  onPick,
}: {
  query: string;
  isSearching: boolean;
  groups: DimensionGroup[];
  rooms: readonly RoomSummary[];
  activeIndex: number;
  flatIndexByEventId: Map<string, number>;
  totalResults: number;
  onPick: (roomId: string, eventId: string) => void;
}) {
  const trimmed = query.trim();
  const showEmptyPrompt = trimmed.length === 0;
  const showNoResults = !showEmptyPrompt && !isSearching && totalResults === 0;
  const roomNameFor = (r: SearchResult): string =>
    rooms.find((room) => room.id === r.roomId)?.name ?? r.roomName;

  return (
    <div
      id="search-results-listbox"
      role="listbox"
      aria-label="Search results"
      style={{
        flex: 1,
        overflowY: "auto",
        background: "var(--chat-surface-dialog)",
      }}
    >
      {showEmptyPrompt && <EmptyPrompt />}
      {isSearching && totalResults === 0 && <LoadingShimmer />}
      {showNoResults && <NoResults query={trimmed} />}
      {!showEmptyPrompt &&
        groups.map((group) => {
          const labelId = `search-group-${group.dimension}`;
          return (
            <div key={group.dimension} role="group" aria-labelledby={labelId}>
              <GroupHeader label={group.label} count={group.results.length} id={labelId} />
              {group.results.map((r) => {
                const flatIndex = flatIndexByEventId.get(r.eventId) ?? -1;
                if (group.dimension === "file") {
                  return (
                    <FileResultItem
                      key={r.eventId}
                      id={`search-result-${r.eventId}`}
                      fileName={r.body}
                      roomName={roomNameFor(r)}
                      active={flatIndex === activeIndex}
                      onJump={() => onPick(r.roomId, r.eventId)}
                    />
                  );
                }
                return (
                  <SearchResultItem
                    key={r.eventId}
                    id={`search-result-${r.eventId}`}
                    senderDisplayName={r.senderDisplayName || r.sender}
                    time={formatMessageTime(r.ts)}
                    roomName={roomNameFor(r)}
                    body={r.body}
                    highlight={trimmed}
                    active={flatIndex === activeIndex}
                    onJump={() => onPick(r.roomId, r.eventId)}
                  />
                );
              })}
            </div>
          );
        })}
    </div>
  );
}

interface DimensionGroup {
  dimension: SearchResultKind;
  label: string;
  results: SearchResult[];
}

const SEARCH_DIMENSIONS: { dimension: SearchResultKind; label: string }[] = [
  { dimension: "message", label: "Messages" },
  { dimension: "file", label: "Files" },
];

function groupByDimension(results: readonly SearchResult[]): DimensionGroup[] {
  const buckets = new Map<SearchResultKind, SearchResult[]>();
  for (const r of results) {
    const dimension: SearchResultKind = r.kind === "file" ? "file" : "message";
    const bucket = buckets.get(dimension);
    if (bucket) bucket.push(r);
    else buckets.set(dimension, [r]);
  }
  return SEARCH_DIMENSIONS.flatMap(({ dimension, label }) => {
    const bucket = buckets.get(dimension);
    return bucket && bucket.length > 0 ? [{ dimension, label, results: bucket }] : [];
  });
}

function GroupHeader({ label, count, id }: { label: string; count: number; id?: string }) {
  return (
    <div
      id={id}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        height: 32,
        padding: "0 var(--chat-space-4)",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "10px",
        lineHeight: "var(--chat-type-body-xs-line)",
        textTransform: "uppercase",
        letterSpacing: "0.12em",
      }}
    >
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      <span style={{ color: "var(--chat-fg-muted)" }}>{count}</span>
    </div>
  );
}

function EmptyPrompt() {
  return (
    <EmptyInline
      title="Find anything you've shared"
      body="Start typing to search across all your rooms."
    />
  );
}

function NoResults({ query }: { query: string }) {
  return (
    <EmptyInline
      title={`No matches for “${query}”`}
      body="Try different keywords or remove the room filter."
    />
  );
}

function EmptyInline({ title, body }: { title: string; body: string }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--chat-space-3)",
        padding: "var(--chat-space-10) var(--chat-space-6)",
        textAlign: "center",
      }}
    >
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--chat-fg-muted)",
        }}
      >
        <Search size={28} strokeWidth={ICON_STROKE} aria-hidden />
      </span>
      <div
        style={{
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-display)",
          fontSize: "var(--chat-type-display-sm-size)",
          lineHeight: "var(--chat-type-display-sm-line)",
          fontWeight: 300,
          maxWidth: 320,
        }}
      >
        {title}
      </div>
      <div
        style={{
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-md-size)",
          lineHeight: "var(--chat-type-body-md-line)",
          maxWidth: 320,
        }}
      >
        {body}
      </div>
    </div>
  );
}

function LoadingShimmer() {
  // The 24×24 avatar, 10px bar height, and 6px column gap match the
  // brief's pinned sizes for this surface (avatar matches
  // `SearchResultItem`, bars are a stock skeleton proportion). Keep
  // the raw px here rather than introduce one-use skeleton tokens.
  return (
    <div role="status" aria-live="polite" aria-label="Searching">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--chat-space-3)",
            height: "var(--chat-search-dialog-result-row-height)",
            padding: "0 var(--chat-space-4)",
          }}
        >
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: "var(--chat-radius-full)",
              background: "var(--chat-surface-panel)",
            }}
          />
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "var(--chat-space-1)",
            }}
          >
            <span
              style={{
                height: 10,
                width: `${60 + ((i * 7) % 30)}%`,
                background: "var(--chat-surface-panel)",
                borderRadius: "var(--chat-radius-full)",
              }}
            />
            <span
              style={{
                height: 10,
                width: `${80 - ((i * 5) % 25)}%`,
                background: "var(--chat-surface-panel)",
                borderRadius: "var(--chat-radius-full)",
                opacity: 0.7,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── footer ──────────────────────────────────────────────────── */

function Footer({ onLoadMore }: { onLoadMore: () => void }) {
  return (
    <div
      style={{
        height: "var(--chat-search-dialog-footer-height)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderTop: "1px solid var(--chat-surface-border)",
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        onClick={onLoadMore}
        className="chat-button"
        style={{
          height: 32,
          padding: "0 var(--chat-space-3)",
          borderRadius: "var(--chat-radius-sm)",
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        Load more results
      </button>
    </div>
  );
}
